import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Card, Field } from "../common/UI";
import { Select } from "../../pages/MeasureCreate";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

const Item = ({ k, v }) => (
  <div>
    <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{k}</dt>
    <dd className="mt-0.5 whitespace-pre-line text-sm text-slate-800">{v || "—"}</dd>
  </div>
);

const EDITABLE = ["short_title", "abstract", "statement_of_purpose", "background", "author_legislator_id", "principal_sponsor_id", "originating_office_id",
  "assigned_office_id", "assigned_user_id", "priority", "committee_id", "session_reference", "legal_basis", "budgetary_implications", "confidentiality"];

function EditDialog({ m, onClose, onSaved }) {
  const [lk, setLk] = useState(null);
  const [f, setF] = useState(() => ({
    ...Object.fromEntries(EDITABLE.map((k) => [k, m[k] || ""])),
    co_sponsor_ids: m.sponsors.filter((s) => s.role === "co").map((s) => s.legislator_id), subjects: m.subjects.join(", "),
  }));
  useEffect(() => { api.get("/lookups").then((r) => setLk(r.data)); }, []);
  const up = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    const payload = { ...f, row_version: m.row_version, subjects: f.subjects.split(",").map((s) => s.trim()).filter(Boolean) };
    EDITABLE.forEach((k) => payload[k] === "" && (payload[k] = null));
    payload.priority = payload.priority || "normal";
    payload.confidentiality = payload.confidentiality || "internal";
    try {
      await api.put(`/measures/${m.id}`, payload);
      toast.success("Measure updated");
      onClose();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const ta = (k, l) => <Field label={l} className="md:col-span-2"><textarea rows={2} className="otms-input" value={f[k]} onChange={up(k)} data-testid={`edit-${k}`} /></Field>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="edit-measure-dialog">
        <DialogHeader><DialogTitle>Edit measure details</DialogTitle></DialogHeader>
        {!lk ? <div className="text-xs">Loading…</div> : (
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Short title"><input className="otms-input" value={f.short_title} onChange={up("short_title")} /></Field>
            <Field label="Subjects"><input className="otms-input" value={f.subjects} onChange={up("subjects")} /></Field>
            {ta("abstract", "Abstract")}{ta("statement_of_purpose", "Statement of purpose")}{ta("background", "Background")}
            <Field label="Author"><Select value={f.author_legislator_id} onChange={up("author_legislator_id")} items={lk.legislators} labelKey="full_name" /></Field>
            <Field label="Principal sponsor"><Select testId="edit-principal-sponsor" value={f.principal_sponsor_id} onChange={up("principal_sponsor_id")} items={lk.legislators} labelKey="full_name" /></Field>
            <Field label="Co-sponsors">
              <select multiple className="otms-input h-20" value={f.co_sponsor_ids} onChange={(e) => up("co_sponsor_ids")([...e.target.selectedOptions].map((o) => o.value))}>
                {lk.legislators.map((l) => <option key={l.id} value={l.id}>{l.full_name}</option>)}
              </select>
            </Field>
            <Field label="Committee"><Select testId="edit-committee" value={f.committee_id} onChange={up("committee_id")} items={lk.committees} /></Field>
            <Field label="Originating office"><Select value={f.originating_office_id} onChange={up("originating_office_id")} items={lk.offices} /></Field>
            <Field label="Assigned office"><Select value={f.assigned_office_id} onChange={up("assigned_office_id")} items={lk.offices} /></Field>
            <Field label="Assigned user"><Select value={f.assigned_user_id} onChange={up("assigned_user_id")} items={lk.users} labelKey="full_name" /></Field>
            <Field label="Session reference"><input className="otms-input" value={f.session_reference} onChange={up("session_reference")} data-testid="edit-session_reference" /></Field>
            <Field label="Priority"><Select value={f.priority} onChange={up("priority")} items={["low", "normal", "high", "urgent"].map((p) => ({ id: p, name: p }))} /></Field>
            <Field label="Classification"><Select value={f.confidentiality} onChange={up("confidentiality")} items={["public", "internal", "confidential", "restricted"].map((p) => ({ id: p, name: p }))} /></Field>
            {ta("legal_basis", "Legal basis")}{ta("budgetary_implications", "Budgetary implications")}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="edit-measure-save">Save changes</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OverviewTab({ m, onSaved }) {
  const { can } = useAuth();
  const nav = useNavigate();
  const [edit, setEdit] = useState(false);
  const del = async () => {
    if (!window.confirm("Delete this draft permanently? The audit trail is retained.")) return;
    try {
      await api.delete(`/measures/${m.id}`);
      toast.success("Draft deleted");
      nav("/registry/all");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const co = m.sponsors.filter((s) => s.role === "co").map((s) => s.name).join(", ");
  return (
    <Card title="Measure profile" testId="overview-tab" actions={
      <div className="flex gap-2">
        {can("measure.delete_draft") && m.status === "draft" && <Button size="sm" variant="ghost" className="text-red-600" onClick={del} data-testid="delete-draft-button"><Trash2 className="h-4 w-4" /></Button>}
        {can("measure.edit") && !m.is_locked && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="edit-measure-button"><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>}
      </div>}>
      <dl className="grid gap-5 md:grid-cols-2">
        <Item k="Proposed title" v={m.proposed_title} /><Item k="Short title" v={m.short_title} />
        <Item k="Term" v={m.summary.term} /><Item k="Filing date" v={fmtDate(m.filing_date)} />
        <Item k="Author" v={m.author} /><Item k="Principal sponsor" v={m.summary.sponsor} />
        <Item k="Co-sponsors" v={co} /><Item k="Committee" v={m.summary.committee} />
        <Item k="Originating office" v={m.originating_office} /><Item k="Assigned to" v={[m.assigned_office, m.assigned_user].filter(Boolean).join(" / ")} />
        <Item k="Filed by" v={m.filed_by} /><Item k="Session reference" v={m.session_reference} />
        <Item k="Subjects" v={m.subjects.join(", ")} /><Item k="Legal references" v={m.legal_references.map((r) => r.citation).join("\n")} />
        <div className="md:col-span-2"><Item k="Abstract" v={m.abstract} /></div>
        <div className="md:col-span-2"><Item k="Statement of purpose" v={m.statement_of_purpose} /></div>
        <div className="md:col-span-2"><Item k="Background and justification" v={m.background} /></div>
        <Item k="Legal basis" v={m.legal_basis} /><Item k="Budgetary implications" v={m.budgetary_implications} />
      </dl>
      {edit && <EditDialog m={m} onClose={() => setEdit(false)} onSaved={onSaved} />}
    </Card>
  );
}
